/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
import { Injectable } from '@angular/core';
import { type BaseRequestOptionsArgs, type DataTableComponent, type FormGroup, type HttpResponse, Browser, CAPACITY_UNIT, DownloadService, HttpService, PermissionService, UtilService } from '@dm/core';
import { type SelectOption } from '@dm/core/types';
import { IdleTaskE, WeeklyE } from 'app/common/constants';
import { DiskLogicTypeE, EncryptDisks, PhonyCapacityOptimizedDiskMap } from 'app/common/constants/disk-type';
import { MO } from 'app/common/constants/mo';
import { ToolService } from 'app/common/services';
import { TaskService } from 'app/common/services/task.service';
import { type DiskInfo, type EraseParams, type IdleRecycleConfig } from 'app/common/types/mo';
import { SWITCH } from 'app/config';
import { FeatureConfig } from 'app/config/feature';
import { cloneDeep } from 'lodash';
import { type Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';

import { type CapacityDisk, type DiskData, type DiskEnclosure, type EngineData, type EngineInfo, type EngineObj, type SelectedDisk, RaidLevelE, raidLevelList } from './model';
import { DEFAULT_HOT_SPARE, DEFAULT_PERFORMANCE_DISK_NUM, HOT_SPARE_COUNT_AND_POLICY_MAP } from './storage-pool.const';
import { Constants, RedundancyStrategyE, StoragePoolStrService } from './storage-pool-str.service';

@Injectable()
export class StoragePoolService {

    defaultHotSpareOptions: SelectOption[];
    // 热备策略
    hotSpareOptions: SelectOption[];

    // 性能层热备策略
    performHotSpareOptions: SelectOption[];
    // 闲时模式
    idleOptions: SelectOption[];
    // 默认热备值
    defaultHotSpare = DEFAULT_HOT_SPARE;

    getRate = this.toolService.getRate;

    constructor(
        private httpService: HttpService,
        private taskService: TaskService,
        private i18nService: StoragePoolStrService,
        private downloadService: DownloadService,
        private permissionService: PermissionService,
        private toolService: ToolService,
        public utilService: UtilService,
    ) {
        this.defaultHotSpareOptions = [
            {
                value: 3,
                label: i18nService.hotSpareStrategy.none,
            },
            {
                value: 1,
                label: i18nService.hotSpareStrategy.low,
            },
            {
                value: 2,
                label: i18nService.hotSpareStrategy.high,
            },
            ...[7, 8, 9, 10, 11, 12].map((count) => (
                {
                    value: count,
                    label: i18nService.hotSpareStrategy.customize(count),
                }
            ))];
        this.hotSpareOptions = [
            ...(FeatureConfig.IsShowDefaultFlashLayerHotSpareOption ? [
                {
                    value: this.defaultHotSpare,
                    label: this.i18nService.createI18n.defaultLabel,
                },
            ] : []),
            ...this.defaultHotSpareOptions,
        ];
        this.performHotSpareOptions = [
            {
                value: this.defaultHotSpare,
                label: this.i18nService.createI18n.defaultLabel,
            },
            ...this.defaultHotSpareOptions,
        ];
        this.idleOptions = [
            {
                value: WeeklyE.workday,
                label: this.i18nService.commonI18n.workday,
            },
            {
                value: WeeklyE.weekend,
                label: this.i18nService.commonI18n.weekend,
            },
            {
                value: WeeklyE.all,
                label: this.i18nService.commonI18n.allDay,
            },
        ];
    }

    // 查询可用控制框信息
    getEngineDiskInfo(id?: number | string | BaseRequestOptionsArgs, oriOptions?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.httpService.makeUrl('diskPool.getEngineDiskinfo');
        let options = oriOptions;

        if (typeof id === 'object') {
            options = id;
        } else {
            rest.setParams({ ID: id });
        }

        return this.httpService.get(rest, options);
    }

    getAllEngineDiskInfo(id?: number | string | BaseRequestOptionsArgs, oriOptions?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.httpService.makeUrl('diskPool.getAllEngineDiskinfo');
        let options = oriOptions;

        if (typeof id === 'object') {
            options = id;
        } else {
            rest.setParams({ ID: id });
        }

        return this.httpService.get(rest, options);
    }

    getAllEngineForFlash(id?: number | string | BaseRequestOptionsArgs, oriOptions?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.httpService.makeUrl('diskPool.getAllEngineForFlash');
        let options = oriOptions;

        if (typeof id === 'object') {
            options = id;
        } else {
            rest.setParams({ id });
        }

        return this.httpService.get(rest, options);
    }

    getReferralInfo(id: number | string | BaseRequestOptionsArgs, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.httpService.makeUrl('diskPool.getReferralInfo');
        rest.setParams({ ID: id });
        return this.httpService.get(rest, options);
    }

    // 查询秘钥服务类型
    getKeyServiceConfig(options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.httpService.makeUrl('keyService.getKmmConfig');
        return this.httpService.get(rest, options);
    }

    // 查询存储池下一个可用ID
    getNextId(): Observable<HttpResponse> {
        const rest = this.httpService.makeUrl('storagePool.nextavailableid');
        rest.setParams({ TYPE: MO.STORAGE_POOL });
        return this.httpService.get(rest, { doException: false });
    }

    // 查询存储池信息
    get(id: number | string, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.httpService.makeUrl('storagePool.get').format(id);
        return this.httpService.get(rest, options);
    }

    // 获取所有存储池
    getAll(options?: BaseRequestOptionsArgs, vstoreId?: string): Observable<HttpResponse> {
        // 直通模式屏蔽pool
        if (SWITCH.IS_DIRECT_MODE) {
            return of(null);
        }
        const rest = this.httpService.makeUrl('storagePool.getBatch');
        if (vstoreId) {
            rest.setParams({ vstoreId });
        }
        return this.httpService.get(rest, options);
    }

    // 获取所有flashLayer
    getAllflashLayer(options?: BaseRequestOptionsArgs, vstoreId?: string): Observable<HttpResponse> {
        const rest = this.httpService.makeUrl('flashLayer.getBatch');
        if (vstoreId) {
            rest.setParams({ vstoreId });
        }
        return this.httpService.get(rest, options);
    }

    // 查询flashLayer
    getFlashLayer(id: number | string, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.httpService.makeUrl('flashLayer.get').format(id);
        rest.setParams({ id });
        return this.httpService.get(rest, options);
    }

    // 查询硬盘域信息
    getDiskDomain(id: number | string, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.httpService.makeUrl('diskPool.get').format(id);
        return this.httpService.get(rest, options);
    }

    // 查询硬盘域任务
    getDiskDomainTask(diskDomainId: number | string, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.httpService.makeUrl('diskDomainTask.get');
        rest.setParams({ POOLID: diskDomainId });
        return this.httpService.get(rest, options);
    }

    // 查询硬盘域的硬盘
    getDisks(diskDomainId: number | string, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.httpService.makeUrl('diskfromdev.getBatch');
        rest.setParams({ POOLID: diskDomainId });
        return this.httpService.get(rest, options);
    }

    // 批量查询硬盘硬盘
    getDisksBatch(params?, options?: BaseRequestOptionsArgs): Observable<DiskInfo[]> {
        const rest = this.httpService.makeUrl('diskfromdev.getBatch');
        if (params) {
            rest.setParams(params);
        }
        return this.toolService.getAllData(rest, options);
    }

    // 批量查控制框信息
    getEnclosureBatch(params?, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.httpService.makeUrl('enclosure.getBatch');
        if (params) {
            rest.setParams(params);
        }
        return this.httpService.get(rest, options);
    }

    /**
     * 预查询存储池容量
     * @param params RAID策略，热备策略等信息
     * @param options 请求options
     * @param usePost 是否通过POST查询
     */
    getAvailableCapacity(params?, options?: BaseRequestOptionsArgs, usePost = false): Observable<HttpResponse> {
        const rest = this.httpService.makeUrl('diskPool.getAvailableCapacity');

        // 需要下发硬盘ID时，使用POST方式
        if (usePost) {
            return this.httpService.post(rest, params, options);
        }

        rest.setParams(params);
        return this.httpService.get(rest, options);
    }

    // 获取flash layer的可用容量
    getFlashLayerAvailableCapacity(params?, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.httpService.makeUrl('flashLayer.getAvailableCapacity');
        rest.setParams(params);
        return this.httpService.get(rest, options);
    }

    // 扩容flash layer
    expandFlashLayer(params?, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.httpService.makeUrl('flashLayer.expand');
        return this.httpService.put(rest, params, options);
    }

    // 创建存储池
    create(objName: string, params: any, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.httpService.makeUrl('storagePool.post', 'V2');
        return this.taskService.post('createStoragePool', objName, rest, params, options);
    }

    // 扩容存储池
    expand(objName: string, id: number | string, params: any, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.httpService.makeUrl('storagePool.expand', 'V2').format(id);
        return this.taskService.put('expandStoragePool', objName, rest, params, options);
    }

    // 重置密钥
    rekey(id: number | string, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.httpService.makeUrl('diskPool.rekey');
        return this.httpService.put(rest, { ID: id, updateEncryptKey: true }, options);
    }

    // 修改存储池
    modify(id: number | string, params: any, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.httpService.makeUrl('storagePool.put').format(id);
        return this.httpService.put(rest, params, options);
    }

    // 修改硬盘域
    modifyDiskPool(id: number | string, params: any, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.httpService.makeUrl('diskPool.modify').format(id);
        return this.httpService.put(rest, params, options);
    }

    modifyDiskDomain(id: number | string, params: any, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.httpService.makeUrl('diskPool.putHotDisk').format(id);
        return this.httpService.put(rest, params, options);
    }

    // 删除存储池
    delete(objName: string, id: number | string, eraseParams: EraseParams, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.httpService.makeUrl('storagePool.delete', 'V2').format(id);
        const erasePatternListStr = JSON.stringify(eraseParams.erasePatternList);
        rest.setParams({
            ...eraseParams,
            erasePatternList: erasePatternListStr,
        });
        return this.taskService.delete('deleteStoragePool', objName, rest, options);
    }

    // 获取性能数据
    getPerformanceData(id: number | string, endTime: number, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.httpService.makeUrl('performanceData.getBatch', 'V2');
        const params = {
            begin_time: (endTime - 3600) * 1000,
            end_time: endTime * 1000,
            compute_mode: 'avg',
            objects: [
                {
                    object_type: MO.STORAGE_POOL,
                    ids: [id],
                    indicators: [21, 22, 370],
                },
            ],
        };
        return this.httpService.post(rest, params, options);
    }

    downloadExportConfig(params, fileName: string): Observable<boolean> {
        const rest = this.httpService.makeUrl('diskPool.exportDownload');
        rest.setParams(params);
        return this.downloadService.download(rest, fileName, 'application/octet-stream');
    }

    /**
     * 根据热备策略获取热备盘数量
     * @param value 热备策略
     * @param reservedCount 预留盘数
     */
    getDiskNumByStrategy = (value: number | string, reservedCount = 0): number => {
        let count = DEFAULT_HOT_SPARE;
        switch (Number(value)) {
            case 3: case 4:
                count = 0;
                break;
            case 1: case 5:
                count = 1;
                break;
            case 2: case 6:
                count = 2;
                break;
            case 7:
            case 8:
            case 9:
            case 10:
            case 11:
            case 12:
                count = (Number(value)) - 4;
                break;
            default:
        }
        return count + reservedCount;
    };

    /**
     * 获取默认的性能层热备策略
     */
    getDefaultHotSpareForFlash(): number {
        return HOT_SPARE_COUNT_AND_POLICY_MAP[Number(Constants.HotSpareDisksMin)] ?? DEFAULT_HOT_SPARE;
    }

    getHotSpareStrategyValue = (value: number | string): number => {
        let result = Number(value);
        switch (Number(value)) {
            case 4:
                result = 3;
                break;
            case 5:
                result = 1;
                break;
            case 6:
                result = 2;
                break;
            default:
                result = Number(value);
        }
        return result;
    };

    renderHotSpareStrategy = (diskNum: number | string): string => {
        let str;
        switch (Number(diskNum)) {
            case DEFAULT_HOT_SPARE:
                str = this.i18nService.hotSpareStrategy.default;
                break;
            case 3: case 4:
                str = this.i18nService.hotSpareStrategy.none;
                break;
            case 1: case 5:
                str = this.i18nService.hotSpareStrategy.low;
                break;
            case 2: case 6:
                str = this.i18nService.hotSpareStrategy.high;
                break;
            case 7:
            case 8:
            case 9:
            case 10:
            case 11:
            case 12:
                str = this.i18nService.hotSpareStrategy.customize(diskNum);
                break;
            default:
        }
        return str;
    };

    /**
     * 获取所有存储池选项
     */
    getStoragePoolOpts(vstoreId?: string): Observable<SelectOption[]> {
        if (!this.permissionService.hasPermission('storage_pool_R')) {
            return of([]);
        }
        return this.getAll(null, vstoreId)
            .pipe(
                map(({ data }) => {
                    const options: SelectOption[] = (data || []).map((a) => ({
                        label: a.NAME,
                        value: a.ID,
                        data: a,
                    }));
                    return options;
                }),
            );
    }

    isEncryptionDisk(disk: Disk): boolean {
        return EncryptDisks.includes(Number(disk.DISKTYPE));
    }

    checkHotSpareCount(control: FormGroup, hotSpareCount: number, diskCounts: number[]): boolean {
        let isValid = true;
        for (const item of diskCounts) {
            if (control?.value && item) {
                const freeDiskCount = item - hotSpareCount;
                isValid = freeDiskCount >= Constants.EachHotSpareEngineDiskMin();
                if (!isValid) {
                    break;
                }
            }
        }
        return isValid;
    }

    /**
     * 是否为OceanProtect产品
     */
    checkProtect(): boolean {
        // V3_6000_806  为了方便内部测试, 设备不对外销售的
        return FeatureConfig.IsShowCompressLevel;
    }

    // 计算盘的容量
    getDiskCap(disks: DiskData[]): number {
        let cap = 0;
        for (const item of disks) {
            cap += item.cap * item.inputDiskNum;
        }
        return cap;
    }

    /**
     * 查询ID表格回收策略
     */
    getIdleStrategy(): Observable<HttpResponse> {
        const rest = this.httpService.makeUrl('idleStrategy.get');
        return this.httpService.get(rest);
    }

    /**
     * 配置ID表格回收策略
     */
    configIdleStrategy(params: IdleRecycleConfig, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.httpService.makeUrl('idleStrategy.put');
        return this.httpService.put(rest, params, options);
    }

    /**
     * 执行ID表回收任务
     */
    startTask(id: number | string, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const param = { ID: id, behavior: IdleTaskE.start };
        const rest = this.httpService.makeUrl('idleStrategy.startTask').format(id);

        return this.httpService.put(rest, param, options);
    }

    /**
     * 停止ID表回收任务
     */
    endTask(id: number | string, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const param = { ID: id, behavior: IdleTaskE.end };
        const rest = this.httpService.makeUrl('idleStrategy.endTask').format(id);
        return this.httpService.put(rest, param, options);
    }

    /**
     * 计算容量
     */
    computeCapacity = (cap: number | string): string => {
        if (cap !== undefined && cap !== '') {
            return this.utilService.adaptUnit(cap, CAPACITY_UNIT.SECTOR).label;
        }

        return '--';
    };

    /**
     * 处理火狐表格异常滑动
     */
    controlDtScroll(enable: boolean, dt: DataTableComponent): void {
        if (Browser.name === 'firefox' && dt) {
            dt.table.scrollbar.instance.enabled = enable;
        }
    }

    /**
     * 获取不同产品支持的RAID级别，Dorado和新融合两种冗余策略都支持RAID10
     */
    getRaidList(redundancy: RedundancyStrategyE): SelectOption[] {
        if (redundancy === RedundancyStrategyE.EnclosureLevel) {
            const raidList = [RaidLevelE.RAID_6, RaidLevelE.RAID_TP, RaidLevelE.RAID_10];
            return raidLevelList.filter((item: SelectOption) => raidList.includes(item.value));
        }

        if (redundancy === RedundancyStrategyE.DiskLevel) {
            const raidList = FeatureConfig.IsSupportRAID10 ?
                [RaidLevelE.RAID_6, RaidLevelE.RAID_TP, RaidLevelE.RAID_10] :
                [RaidLevelE.RAID_6, RaidLevelE.RAID_TP];
            return raidLevelList.filter((item: SelectOption) => raidList.includes(item.value));
        }

        return [];
    }

    /**
     * 检查性能层是否需要默认的热备盘数
     * @param hddNum 容量层盘数
     * @param ssdNum 性能层盘数（每引擎）
     */
    isNeedDefaultHotSpareForFlash(hddNum: number, ssdNum: number): boolean {
        const noHDD = hddNum === 0;

        return noHDD && this.checkFlashSupportDefaultHotSpare(ssdNum);
    }

    /**
     * 检查性能层盘数是否满足自动选择热备策略
     * @param ssdNum 性能层盘数（每引擎）
     */
    checkFlashSupportDefaultHotSpare(ssdNum: number): boolean {
        // 全闪SSD创建的存储池，SSD数量少于8盘，可能需要给一个默认的热备策略
        const needDefaultHotSpare = ssdNum >= Number(Constants.PerFormanceEachEngineDiskMin) &&
            ssdNum < DEFAULT_PERFORMANCE_DISK_NUM;
        const minHotSpare = Number(Constants.HotSpareDisksMin);

        return needDefaultHotSpare && minHotSpare > DEFAULT_HOT_SPARE;
    }

    /**
     * 判断是否支持独享性能层
     */
    supportUniquePerformanceLayer(): boolean {
        return FeatureConfig.IsSupportUniquePerformanceLayer;
    }

    /**
     * 获取选盘容量、数量和id列表
     */
    getSelectedDiskCapNumIdList(selectedDiskArr: DiskData[], selectedEngineNum = 0, selectedDiskEnclosureArr?: DiskEnclosure[]): {
        associateObjIDList: string[];
        diskNumList: string[];
        diskCapacityList: string[];
        performanceLayerDiskType: string;
        frameIdList?: string[];
    } {
        const associateObjIDList: string[] = [];
        const diskNumList: string[] = [];
        const diskCapacityList: string[] = [];
        const frameIdList: string[] = [];
        const tempFrameIdList = selectedDiskEnclosureArr?.map((a) => a.id) ?? [];
        const selectedDiskEnclosureNumber = selectedDiskEnclosureArr?.length;

        // 同时选了多种盘类型，只需要下任意发一种，pool会根据下发的盘类型找到其他符合条件的盘类型
        let performanceLayerDiskType;

        // 框级冗余时会传selectedDiskEnclosureNumber，此时diskCapacityList和diskNumLis传的是已选硬盘个数容量组成的数组而不是总数
        if (selectedDiskEnclosureNumber) {
            selectedDiskArr.forEach((item: DiskData) => {
                item.selection?.forEach((selectedItem: SelectedDisk) => {
                    // 只将“空闲盘”ID放入数组（已存在的硬盘属于“成员盘”），扩容只下发新选的硬盘
                    if (selectedItem.LOGICTYPE === DiskLogicTypeE.Free) {
                        associateObjIDList.push(selectedItem.ID);
                    }
                });

                const capCount = item.selectedDiskNum + item.memberDiskNum;

                if (capCount > 0) {
                    const capValue = this.utilService.adaptUnit(item.cap, CAPACITY_UNIT.SECTOR, CAPACITY_UNIT.GB).value;
                    const cap = capValue.toString().split('.')[0];

                    for (let i = 0; i < selectedDiskEnclosureNumber; i++) {
                        diskCapacityList.push(cap);
                        diskNumList.push(String(capCount / selectedDiskEnclosureNumber));
                    }

                    frameIdList.push(...tempFrameIdList);

                    // 在新增的盘类型列表中赋值，避免下发的type加密类型与选择不一致
                    performanceLayerDiskType = item?.diskTypeValueList && item?.diskTypeValueList[0];
                }
            });

            return {
                associateObjIDList,
                diskNumList,
                diskCapacityList,
                frameIdList,
                performanceLayerDiskType,
            };
        }

        selectedDiskArr.forEach((disk: DiskData) => {
            if (Number(disk.inputDiskNum) === 0) {
                return;
            }

            disk.selection?.forEach((selectedItem: SelectedDisk) => {
                // 只将“空闲盘”ID放入数组（已存在的硬盘属于“成员盘”），扩容只下发新选的硬盘
                if (selectedItem.LOGICTYPE === DiskLogicTypeE.Free) {
                    associateObjIDList.push(selectedItem.ID);
                }
            });
            const capValue = this.utilService.adaptUnit(disk.cap, CAPACITY_UNIT.SECTOR, CAPACITY_UNIT.GB).value;
            const cap = capValue.toString().split('.')[0];
            diskNumList.push(String(disk.inputDiskNum * selectedEngineNum));
            diskCapacityList.push(cap);
            performanceLayerDiskType = disk.diskTypeValueList?.[0];
        });

        return {
            associateObjIDList,
            diskNumList,
            diskCapacityList,
            frameIdList,
            performanceLayerDiskType,
        };
    }

    /**
     * 根据底层返回的硬盘枚举，更新返回数据中的diskType字符串（硬盘19和21，20和22底层返回的diskType分别相同，需要特殊处理）
     */
    updateDiskTypeOfAllEngineInfo(engineInfo: EngineInfo): EngineInfo {
        const updatedEngineInfo = engineInfo;

        if (updatedEngineInfo.freeDisk.length > 0) {
            updatedEngineInfo.freeDisk = this.updatePhonyCapacityOptimizedSsdDiskType(updatedEngineInfo.freeDisk);
        }

        if (updatedEngineInfo.memberDisk.length > 0) {
            updatedEngineInfo.memberDisk = this.updatePhonyCapacityOptimizedSsdDiskType(updatedEngineInfo.memberDisk);
        }

        return updatedEngineInfo;
    }

    /**
     * 更新假容量型硬盘的disktype字段
     */
    updatePhonyCapacityOptimizedSsdDiskType(engineObjArr: EngineObj[]): EngineObj[] {
        const updatedEngineObj = cloneDeep(engineObjArr);

        updatedEngineObj.forEach((engineObj: EngineObj, engineObjIndex: number) => {
            const engineObjkeys = Object.keys(engineObj);

            engineObjkeys.forEach((engine: string) => {
                engineObj[engine].forEach((disk: CapacityDisk, diskIndex) => {
                    if (PhonyCapacityOptimizedDiskMap.get(Number(disk.diskTypeValue))) {
                        updatedEngineObj[engineObjIndex][engine][diskIndex].diskType =
                            PhonyCapacityOptimizedDiskMap.get(Number(disk.diskTypeValue));
                    }
                });
            });
        });

        return updatedEngineObj;
    }

    /**
     * 是否支持AP StoragePool（微存储直通模式不支持）
     */
    isSupportAPStoragePool = (): boolean => FeatureConfig.IsSupportDataAffinityMode && !SWITCH.IS_DIRECT_MODE;

    /**
     * 对引擎进行排序
     * @param engineArr 引擎数组
     */
    sortEngine(engineArr: EngineData[]): EngineData[] {
        // 如果ID都是数字，则使用数字顺序排序
        const isAllNum = engineArr.every((engine) => !isNaN(Number(engine.engineId)) && engine.engineId?.trim() !== '');
        if (isAllNum) {
            return engineArr.sort((a, b) => Number(a.engineId) - Number(b.engineId));
        }

        // 否则使用原方式
        return engineArr.sort((a, b) => a.engineId.localeCompare(b.engineId));
    }
}
